import { forwardRef, useState, Suspense, lazy, useRef, useImperativeHandle } from 'react';
import { warning } from '@ux/js';
import { cx } from 'linaria';
import { classNames as classNames$3, Smallprint } from '@uds/typography';
import './common.stylesheet_ga2x69.css';
import './generic.stylesheet_qklf5z.css';
import './input.stylesheet_13y5qdw.css';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import './strengthindicator.stylesheet_749gcv.css';
import { Glyph } from '@uds/glyphs';
import './togglebutton.stylesheet_1ogpsza.css';
import './searchinput.stylesheet_gdebgh.css';

let Size;
(function (Size) {
  Size["SMALL"] = "SMALL";
  Size["MEDIUM"] = "MEDIUM";
  Size["LARGE"] = "LARGE";
})(Size || (Size = {}));
let PasswordStrength;
(function (PasswordStrength) {
  PasswordStrength["EMPTY"] = "none";
  PasswordStrength["TERRIBLE"] = "0";
  PasswordStrength["WEAK"] = "1";
  PasswordStrength["FAIR"] = "2";
  PasswordStrength["GOOD"] = "3";
  PasswordStrength["STRONG"] = "4";
})(PasswordStrength || (PasswordStrength = {}));

const isDisabled = "i1ybf02r";

const subroot = "s111osgp";
const subrootFocus = "s3v1dt9";
const subRootError = "sx0u1n6";
const withPrecursor = "w1oqcd5w";
const prefixSuffix = "p1f15n9x";
const prefix = "p19pvv4o";
const suffix = "s1xhuibv";
const action = "a1ovsycb";
const root$1 = "r1akvuuf";
const withBottomAddon = "w54gg38";
const error = "e1iwcedp";
const getClassNames = _ref => {
  let {
    hasBottomAddon,
    disabled,
    size,
    hasPrecursor,
    hasError,
    hasFocus
  } = _ref;
  return {
    root: cx(root$1, size === Size.LARGE ? classNames$3.headingXs : classNames$3.content),
    subroot: cx(subroot, disabled && isDisabled, hasPrecursor && withPrecursor, hasError && subRootError, hasFocus && subrootFocus, hasBottomAddon && withBottomAddon),
    prefix: cx(prefixSuffix, prefix, classNames$3.headingXxs),
    suffix: cx(prefixSuffix, suffix, classNames$3.headingXxs),
    action: cx(action),
    error: cx(error, classNames$3.footnote)
  };
};

const input$1 = "i187tvht";

const Input = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    className,
    ...inputProps
  } = _ref;
  return /*#__PURE__*/jsx("input", {
    ...inputProps,
    ref: ref,
    className: cx(className, input$1, inputProps.disabled && isDisabled)
  });
});
Input.displayName = 'Input';

/**
 * Abstracting away the logic from the input component.
 */
function useGenericInput(_ref) {
  let {
    error,
    id,
    onFocus,
    onBlur
  } = _ref;
  /*
   * Just using pure css is not enough here, we don't want to apply focus styles to the input
   * when the input action has focus.
   */
  const [hasFocus, setHasFocus] = useState(false);
  const hasError = error != null;
  const onFocusHandler = e => {
    setHasFocus(true);
    if (onFocus) {
      onFocus(e);
    }
  };
  const onBlurHandler = e => {
    setHasFocus(false);
    if (onBlur) {
      onBlur(e);
    }
  };
  return {
    models: {
      hasFocus,
      hasError,
      errorId: hasError ? `${id}-error` : null
    },
    operations: {
      onFocusHandler,
      onBlurHandler
    }
  };
}

const GenericAction = /*#__PURE__*/lazy(() => import('./GenericAction-9013ed21.js'));
const Generic = (_ref, ref) => {
  let {
    precursor,
    bottomAddon,
    withPadding,
    prefix,
    suffix,
    action,
    inputSize = Size.MEDIUM,
    error,
    onFocus,
    onBlur,
    ...inputProps
  } = _ref;
  const {
    models: {
      hasError,
      hasFocus,
      errorId
    },
    operations: {
      onBlurHandler,
      onFocusHandler
    }
  } = useGenericInput({
    error,
    onFocus,
    onBlur,
    id: inputProps.id
  });
  warning(inputProps.id != null, `Input with name ${inputProps.name} was rendered without an id. All inputs should have id`);
  const hasAction = action != null;
  const classNames = getClassNames({
    disabled: inputProps.disabled,
    size: inputSize,
    hasPrecursor: precursor != null,
    hasError,
    hasFocus,
    hasBottomAddon: bottomAddon != null
  });
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsxs("span", {
      className: classNames.root,
      "data-size": inputSize,
      "data-has-action": hasAction ? 'TRUE' : 'FALSE',
      children: [precursor, /*#__PURE__*/jsxs("div", {
        className: classNames.subroot,
        children: [prefix != null && prefix !== '' && /*#__PURE__*/jsx("span", {
          "aria-hidden": "true",
          className: classNames.prefix,
          children: prefix
        }), /*#__PURE__*/jsx(Input, {
          ...inputProps,
          onFocus: onFocusHandler,
          onBlur: onBlurHandler,
          "aria-describedby": [inputProps['aria-describedby'], errorId].filter(Boolean).join(' '),
          ref: ref
        }), suffix != null && suffix !== '' && /*#__PURE__*/jsx("span", {
          "aria-hidden": "true",
          className: classNames.suffix,
          children: suffix
        }), hasAction && /*#__PURE__*/jsx(Suspense, {
          fallback: null,
          children: /*#__PURE__*/jsx(GenericAction, {
            className: classNames.action,
            children: action
          })
        })]
      })]
    }), bottomAddon, errorId != null && /*#__PURE__*/jsx("div", {
      id: errorId,
      className: classNames.error,
      children: error
    })]
  });
};
var Generic$1 = /*#__PURE__*/forwardRef(Generic);

// Storybook has problems using enums inside linaria definitions.
const root = "r1tsb8w6";
const bar = "b336gys";
const track = "t1dfmz4n";
const description = "dbf0oce";
const classNames$2 = {
  root,
  bar,
  track,
  description
};

const StrengthIndicator = _ref => {
  let {
    strength,
    passwordDescription
  } = _ref;
  return /*#__PURE__*/jsxs("div", {
    className: classNames$2.root,
    "aria-live": "polite",
    children: [/*#__PURE__*/jsx("div", {
      className: classNames$2.track,
      children: /*#__PURE__*/jsx("div", {
        "data-strength": strength,
        className: classNames$2.bar
      })
    }), /*#__PURE__*/jsx(Smallprint, {
      className: classNames$2.description,
      children: passwordDescription
    })]
  });
};

const flex = "f1t2q4f7";
const button = "b1qv7zoy";
const glyph = "g9rjzkc";
const classNames$1 = {
  button: cx(flex, button),
  glyph: cx(glyph, flex)
};

const ToggleButton = _ref => {
  let {
    showPassword,
    onClick,
    ariaLabel
  } = _ref;
  return /*#__PURE__*/jsx("button", {
    type: "button",
    onClick: onClick,
    className: classNames$1.button,
    "aria-label": ariaLabel,
    children: /*#__PURE__*/jsx(Glyph, {
      className: classNames$1.glyph,
      name: showPassword ? 'eyeStrikethrough' : 'eye'
    })
  });
};

function usePasswordInput(_ref) {
  let {
    showPasswordAriaLabel,
    hidePasswordAriaLabel,
    ref
  } = _ref;
  const inputRef = useRef(null);
  useImperativeHandle(ref, () => inputRef === null || inputRef === void 0 ? void 0 : inputRef.current);
  const [showPassword, setShowPassword] = useState(false);
  const toggleShowPassword = () => {
    const el = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current;
    if (el != null) {
      el.focus();
    }
    setShowPassword(show => !show);
  };
  return {
    models: {
      showPassword,
      inputType: showPassword ? 'text' : 'password',
      buttonAriaLabel: showPassword ? hidePasswordAriaLabel : showPasswordAriaLabel,
      inputRef
    },
    operations: {
      toggleShowPassword
    }
  };
}

const PasswordInput = (_ref, ref) => {
  let {
    showPasswordAriaLabel,
    hidePasswordAriaLabel,
    passwordDescription,
    strength,
    ...rest
  } = _ref;
  const {
    models: {
      inputType,
      showPassword,
      buttonAriaLabel,
      inputRef
    },
    operations: {
      toggleShowPassword
    }
  } = usePasswordInput({
    showPasswordAriaLabel,
    hidePasswordAriaLabel,
    ref
  });
  return /*#__PURE__*/jsx(Generic$1, {
    type: inputType,
    action: /*#__PURE__*/jsx(ToggleButton, {
      showPassword: showPassword,
      onClick: toggleShowPassword,
      ariaLabel: buttonAriaLabel
    }),
    ...rest,
    ref: inputRef,
    bottomAddon: /*#__PURE__*/jsx(StrengthIndicator, {
      passwordDescription: passwordDescription,
      strength: strength
    })
  });
};
var PasswordInput$1 = /*#__PURE__*/forwardRef(PasswordInput);

const input = "i4ruenx";
const classNames = {
  input
};

const SearchInput = _ref => {
  let {
    suffix = null,
    prefix = null,
    disabled = false,
    inputProps,
    action,
    error,
    placeholder
  } = _ref;
  return /*#__PURE__*/jsx(Generic$1, {
    className: classNames.input,
    inputSize: Size.LARGE,
    suffix: suffix,
    prefix: prefix,
    placeholder: placeholder,
    error: error,
    action: action,
    disabled: disabled,
    ...inputProps
  });
};

export { Generic$1 as Input, PasswordInput$1 as PasswordInput, SearchInput, Size };
